/* ============================================================
   Nature Nook — animation engine
   A9: staggered scroll reveal (IntersectionObserver)
   A1/A2: marquee track duplication for seamless loops
   All effects disabled under prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- A9: scroll reveal ----------
     Usage: add .nn-reveal to any element.
     Add data-nn-reveal-group to a parent to stagger its .nn-reveal children. */
  /* Auto-animate everything: tag common elements that lack .nn-reveal */
  var AUTO_REVEAL_SELECTORS = [
    '.nn-card',
    '.nn-section-head',
    '.nn-lookbook__tile',
    '.nn-testimonials__card',
    '.nn-reels__tile',
    '.nn-story__feature',
    '.nn-footer__brand',
    '.nn-footer__col',
    '.nn-footer__newsletter',
    '.nn-coll-card',
    '.nn-coll__head',
    '.nn-breadcrumbs',
    '.product__media-item',
    '.nn-acc__item'
  ].join(', ');

  function initReveal(root) {
    var scope = root || document;
    scope.querySelectorAll(AUTO_REVEAL_SELECTORS).forEach(function (el) {
      if (!el.classList.contains('nn-reveal') && !el.classList.contains('is-revealed')) {
        el.classList.add('nn-reveal');
      }
    });
    var els = scope.querySelectorAll('.nn-reveal:not(.is-revealed)');
    if (!els.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    document.querySelectorAll('[data-nn-reveal-group]').forEach(function (group) {
      var children = group.querySelectorAll('.nn-reveal');
      var step = parseInt(group.getAttribute('data-nn-reveal-stagger') || '100', 10);
      children.forEach(function (child, i) {
        child.style.setProperty('--nn-reveal-delay', i * step + 'ms');
      });
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    els.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------- A1/A2: marquee ----------
     Markup: .nn-marquee > .nn-marquee__track (content once).
     The track is cloned until it spans >= 2x the viewport so the
     -100% translate loop is seamless at any screen width. */
  function initMarquees(root) {
    (root || document).querySelectorAll('.nn-marquee:not([data-nn-ready])').forEach(function (marquee) {
      var track = marquee.querySelector('.nn-marquee__track');
      if (!track) return;
      marquee.setAttribute('data-nn-ready', 'true');
      if (reducedMotion) return;

      var trackWidth = track.scrollWidth;
      var target = Math.max(window.innerWidth, marquee.offsetWidth);
      if (trackWidth === 0) return;

      var copiesNeeded = Math.max(1, Math.ceil(target / trackWidth));
      var content = track.innerHTML;
      for (var i = 0; i < copiesNeeded; i++) track.innerHTML += content;

      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      marquee.appendChild(clone);
    });
  }

  /* ---------- A10: carousel (progress bar + arrows) ---------- */
  function initCarousels(root) {
    (root || document).querySelectorAll('[data-nn-carousel]:not([data-nn-ready])').forEach(function (carousel) {
      carousel.setAttribute('data-nn-ready', 'true');
      var viewport = carousel.querySelector('[data-nn-carousel-viewport]');
      var bar = carousel.querySelector('[data-nn-carousel-bar]');
      var prev = carousel.querySelector('[data-nn-carousel-prev]');
      var next = carousel.querySelector('[data-nn-carousel-next]');
      if (!viewport) return;

      function maxScroll() {
        return viewport.scrollWidth - viewport.clientWidth;
      }

      function update() {
        var max = maxScroll();
        if (bar) {
          var visible = viewport.clientWidth / viewport.scrollWidth;
          var progress = max > 0 ? viewport.scrollLeft / max : 0;
          var width = Math.max(visible * 100, 8);
          bar.style.width = width + '%';
          bar.style.marginLeft = progress * (100 - width) + '%';
        }
        if (prev) prev.toggleAttribute('disabled', viewport.scrollLeft <= 2);
        if (next) next.toggleAttribute('disabled', viewport.scrollLeft >= max - 2);
      }

      function step() {
        var item = viewport.querySelector('.nn-carousel__item');
        return item ? item.offsetWidth + 12 : viewport.clientWidth * 0.8;
      }

      if (prev) prev.addEventListener('click', function () {
        viewport.scrollBy({ left: -step(), behavior: reducedMotion ? 'auto' : 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        viewport.scrollBy({ left: step(), behavior: reducedMotion ? 'auto' : 'smooth' });
      });
      viewport.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- A5: featured collections interactive list ---------- */
  function initFeaturedCollections(root) {
    (root || document).querySelectorAll('[data-nn-fc]:not([data-nn-ready])').forEach(function (fc) {
      fc.setAttribute('data-nn-ready', 'true');
      var items = fc.querySelectorAll('[data-nn-fc-item]');

      function activate(index) {
        ['item', 'image', 'desc'].forEach(function (part) {
          fc.querySelectorAll('[data-nn-fc-' + part + ']').forEach(function (el) {
            el.classList.toggle('is-active', el.getAttribute('data-nn-fc-' + part) === String(index));
          });
        });
      }

      items.forEach(function (item) {
        var index = item.getAttribute('data-nn-fc-item');
        item.addEventListener('mouseenter', function () { activate(index); });
        item.addEventListener('focus', function () { activate(index); });
      });
    });
  }

  /* ---------- A6: tabbed product grids ---------- */
  function initTabs(root) {
    (root || document).querySelectorAll('[data-nn-tabs]:not([data-nn-ready])').forEach(function (tabbar) {
      tabbar.setAttribute('data-nn-ready', 'true');
      var scope = tabbar.closest('.nn-curated__products') || document;

      tabbar.querySelectorAll('[data-nn-tab]').forEach(function (tab) {
        tab.addEventListener('click', function () {
          var index = tab.getAttribute('data-nn-tab');
          tabbar.querySelectorAll('[data-nn-tab]').forEach(function (t) {
            var active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          scope.querySelectorAll('[data-nn-tab-panel]').forEach(function (panel) {
            var active = panel.getAttribute('data-nn-tab-panel') === index;
            panel.classList.toggle('is-active', active);
            panel.classList.remove('is-entering');
            if (active) {
              void panel.offsetWidth;
              panel.classList.add('is-entering');
              panel.querySelectorAll('.nn-reveal').forEach(function (el) {
                el.classList.add('is-revealed');
              });
            }
          });
        });
      });

      /* Cards in hidden panels never intersect — reveal them up front */
      scope.querySelectorAll('[data-nn-tab-panel]:not(.is-active) .nn-reveal').forEach(function (el) {
        el.classList.add('is-revealed');
      });
    });
  }

  /* ---------- A7: auto-rotating promo slideshow ---------- */
  function initSlideshows(root) {
    (root || document).querySelectorAll('[data-nn-slideshow]:not([data-nn-ready])').forEach(function (show) {
      show.setAttribute('data-nn-ready', 'true');
      var slides = show.querySelectorAll('[data-nn-slide]');
      if (slides.length < 2) return;
      var dots = show.querySelectorAll('[data-nn-slide-dot]');
      var interval = parseInt(show.getAttribute('data-nn-slideshow-interval') || '5000', 10);
      var current = 0;
      var timer = null;

      var hasEffect = show.hasAttribute('data-nn-slideshow-effect');

      function goTo(index) {
        var previous = current;
        current = (index + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.classList.remove('is-leaving');
          if (hasEffect && i === previous && previous !== current) {
            slide.classList.add('is-leaving');
            setTimeout(function () {
              slide.classList.remove('is-leaving');
            }, 750);
          }
          slide.classList.toggle('is-active', i === current);
        });
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === current);
        });
      }

      function play() {
        if (reducedMotion) return;
        stop();
        timer = setInterval(function () { goTo(current + 1); }, interval);
      }
      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          goTo(i);
          play();
        });
      });
      show.addEventListener('mouseenter', stop);
      show.addEventListener('mouseleave', play);
      play();
    });
  }

  /* ---------- A8: live countdown ---------- */
  function initCountdowns(root) {
    (root || document).querySelectorAll('[data-nn-countdown]:not([data-nn-ready])').forEach(function (el) {
      el.setAttribute('data-nn-ready', 'true');
      var end = parseInt(el.getAttribute('data-nn-countdown-end'), 10) * 1000;
      var fields = {
        days: el.querySelector('[data-nn-cd-days]'),
        hours: el.querySelector('[data-nn-cd-hours]'),
        mins: el.querySelector('[data-nn-cd-mins]'),
        secs: el.querySelector('[data-nn-cd-secs]')
      };

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      function tick() {
        var diff = Math.max(0, end - Date.now());
        var s = Math.floor(diff / 1000);
        if (fields.days) fields.days.textContent = pad(Math.floor(s / 86400));
        if (fields.hours) fields.hours.textContent = pad(Math.floor((s % 86400) / 3600));
        if (fields.mins) fields.mins.textContent = pad(Math.floor((s % 3600) / 60));
        if (fields.secs) fields.secs.textContent = pad(s % 60);
        if (diff <= 0 && el.dataset.nnDone !== 'true') {
          el.dataset.nnDone = 'true';
          clearInterval(timer);
        }
      }

      var timer = setInterval(tick, 1000);
      tick();
    });
  }

  /* ---------- A10: drag-to-scroll for horizontal rows ---------- */
  var DRAG_SELECTORS = '[data-nn-carousel-viewport], .nn-testimonials__row, .nn-reels__row';
  function initDragScroll(root) {
    (root || document).querySelectorAll(DRAG_SELECTORS).forEach(function (row) {
      if (row.hasAttribute('data-nn-drag-ready')) return;
      row.setAttribute('data-nn-drag-ready', 'true');
      row.classList.add('nn-draggable');

      var isDown = false;
      var moved = false;
      var startX = 0;
      var startScroll = 0;

      row.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        isDown = true;
        moved = false;
        startX = e.clientX;
        startScroll = row.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!isDown) return;
        var dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 12) {
          moved = true;
          row.classList.add('is-dragging');
        }
        if (moved) row.scrollLeft = startScroll - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!isDown) return;
        isDown = false;
        row.classList.remove('is-dragging');
      });
      /* swallow the click that ends a drag so links don't fire */
      row.addEventListener('click', function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      }, true);
    });
  }

  /* ---------- Quick add to cart from product cards ---------- */
  function initQuickAdd(root) {
    (root || document).querySelectorAll('[data-nn-quick-add]:not([data-nn-ready])').forEach(function (wrap) {
      wrap.setAttribute('data-nn-ready', 'true');

      var showBtn = wrap.querySelector('[data-nn-show-variants]');
      var panel = wrap.querySelector('[data-nn-variant-panel]');
      var frame = wrap.closest('.nn-card__frame');

      /* Fallback: when pointerdown/up land on different elements (fast
         synthetic clicks, first tap on touch), the click resolves to the
         frame — re-dispatch it to whichever control sits under the point. */
      if (frame) {
        frame.addEventListener('click', function (e) {
          if (e.target.closest('[data-nn-show-variants], [data-nn-atc]')) return;
          if (e.target.closest('a')) return;
          var el = document.elementFromPoint(e.clientX, e.clientY);
          var control = el && el.closest('[data-nn-show-variants], [data-nn-atc]');
          if (control) {
            e.preventDefault();
            control.click();
          }
        });
      }

      if (showBtn && panel) {
        showBtn.addEventListener('click', function () {
          showBtn.hidden = true;
          panel.hidden = false;
          wrap.classList.add('is-open');
        });
        if (frame) {
          frame.addEventListener('mouseleave', function () {
            panel.hidden = true;
            showBtn.hidden = false;
            wrap.classList.remove('is-open');
          });
        }
      }

      wrap.querySelectorAll('[data-nn-atc]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled || btn.classList.contains('is-loading')) return;
          var variantId = btn.getAttribute('data-nn-variant-id');
          var label = btn.textContent;
          btn.classList.add('is-loading');
          btn.textContent = 'Adding…';

          fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url + '.js' : '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
          })
            .then(function (res) {
              if (!res.ok) throw new Error('add failed');
              return fetch('/cart.js').then(function (r) { return r.json(); });
            })
            .then(function (cart) {
              btn.classList.remove('is-loading');
              btn.classList.add('is-added');
              btn.textContent = 'Added ✓';
              document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(function (el) {
                el.textContent = cart.item_count;
              });
              /* if the cart icon has no bubble yet, reload it lazily */
              var bubbleHost = document.getElementById('cart-icon-bubble');
              if (bubbleHost && !bubbleHost.querySelector('.cart-count-bubble')) {
                var bubble = document.createElement('div');
                bubble.className = 'cart-count-bubble';
                bubble.innerHTML = '<span aria-hidden="true">' + cart.item_count + '</span>';
                bubbleHost.appendChild(bubble);
              }
              setTimeout(function () {
                btn.classList.remove('is-added');
                btn.textContent = label;
                if (showBtn && panel) {
                  panel.hidden = true;
                  showBtn.hidden = false;
                  wrap.classList.remove('is-open');
                }
              }, 1600);
            })
            .catch(function () {
              btn.classList.remove('is-loading');
              btn.textContent = 'Unavailable';
              setTimeout(function () {
                btn.textContent = label;
              }, 1600);
            });
        });
      });
    });
  }

  /* ---------- A13: accordions ---------- */
  function initAccordions(root) {
    (root || document).querySelectorAll('[data-nn-acc]:not([data-nn-ready])').forEach(function (item) {
      item.setAttribute('data-nn-ready', 'true');
      var trigger = item.querySelector('[data-nn-acc-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', function () {
        var open = item.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---------- A14: reels hover autoplay ---------- */
  function initReels(root) {
    (root || document).querySelectorAll('[data-nn-reel]:not([data-nn-ready])').forEach(function (tile) {
      tile.setAttribute('data-nn-ready', 'true');
      var video = tile.querySelector('video');
      if (!video || reducedMotion) return;
      tile.addEventListener('mouseenter', function () {
        video.play().catch(function () {});
      });
      tile.addEventListener('mouseleave', function () {
        video.pause();
      });
    });
  }

  /* ---------- A15: sticky mini add-to-cart ---------- */
  function initStickyAtc(root) {
    (root || document).querySelectorAll('[data-nn-sticky-atc]:not([data-nn-ready])').forEach(function (bar) {
      bar.setAttribute('data-nn-ready', 'true');
      var buyBox = document.querySelector('.product__info-container, .product__info-wrapper, product-info');
      var mainForm = document.querySelector('product-form form[action*="/cart/add"], form[action*="/cart/add"]');
      var btn = bar.querySelector('[data-nn-sticky-atc-btn]');

      if (btn) {
        btn.addEventListener('click', function () {
          if (mainForm) {
            if (typeof mainForm.requestSubmit === 'function') mainForm.requestSubmit();
            else mainForm.submit();
          } else {
            window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
          }
        });
      }

      if (!buyBox || !('IntersectionObserver' in window)) return;
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var show = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            bar.classList.toggle('is-visible', show);
            bar.setAttribute('aria-hidden', show ? 'false' : 'true');
          });
        },
        { threshold: 0 }
      );
      observer.observe(buyBox);
    });
  }

  /* ---------- 15: mega menu open/close (A11) ---------- */
  function initMegaMenu(root) {
    (root || document).querySelectorAll('[data-nn-mega]:not([data-nn-ready])').forEach(function (mega) {
      mega.setAttribute('data-nn-ready', 'true');
      var triggerText = (mega.getAttribute('data-nn-mega-trigger') || '').trim().toLowerCase();
      var header = mega.closest('.header-wrapper') || document;
      var triggers = [];
      header.querySelectorAll('.header__menu-item, nav a').forEach(function (link) {
        if (link.textContent.trim().toLowerCase() === triggerText) triggers.push(link);
      });
      if (!triggers.length) return;

      var closeTimer = null;
      function open() {
        clearTimeout(closeTimer);
        mega.classList.add('is-open');
      }
      function scheduleClose() {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(function () {
          mega.classList.remove('is-open');
        }, 180);
      }

      triggers.concat([mega]).forEach(function (el) {
        el.addEventListener('mouseenter', open);
        el.addEventListener('mouseleave', scheduleClose);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') mega.classList.remove('is-open');
      });
    });
  }

  function initAll() {
    initReveal();
    initMarquees();
    initCarousels();
    initFeaturedCollections();
    initTabs();
    initSlideshows();
    initCountdowns();
    initAccordions();
    initReels();
    initStickyAtc();
    initMegaMenu();
    initDragScroll();
    initQuickAdd();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Re-init when the theme editor injects/reloads sections */
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    initMarquees(e.target);
    initCarousels(e.target);
    initFeaturedCollections(e.target);
    initTabs(e.target);
    initSlideshows(e.target);
    initCountdowns(e.target);
    initAccordions(e.target);
    initReels(e.target);
    initStickyAtc(e.target);
    initMegaMenu(e.target);
    initDragScroll(e.target);
    initQuickAdd(e.target);
  });

  window.NNAnimations = {
    initReveal: initReveal,
    initMarquees: initMarquees,
    initCarousels: initCarousels,
    initFeaturedCollections: initFeaturedCollections,
    initTabs: initTabs,
    initSlideshows: initSlideshows
  };
})();
